// "Volver a la fuente": borrar el valor de master de un campo para que el
// producto vuelva a mostrar lo que diga la siguiente fuente de la prioridad.
//
// Se hace con el PATCH de siempre (`PATCH /products/{sku}`) mandando `null` en
// ese campo. Backend: revisión hub-service-00228.
//
// Lo que este módulo resuelve son las dos trampas del contrato:
//
// 1. NO todo campo se puede soltar solo. Las hojas obligatorias de un objeto
//    (el `value` y la `unit` de una medición, el precio base) se rechazan con
//    400: hay que soltar el OBJETO que las contiene. O sea que el botón de
//    `/attributes/…/rim/value` tiene que mandar `rim: null`, no `value: null`.
// 2. Hay campos que no se sueltan de ninguna forma — `sku` y las raíces
//    `attributes`/`metadata` —, y ahí no va botón. `isActive` sí (back
//    5467e65): `null` borra el cambio manual y deciden las reglas.

/** Campos que el back nunca deja soltar, sin alternativa. */
const NEVER_RELEASABLE = new Set(['/sku', '/attributes', '/metadata']);

/**
 * Hojas obligatorias dentro de un objeto: soltarlas de a una da 400. El back
 * nombra `value`/`unit` de las mediciones (dimensiones y las de
 * extendedProperties) y de `warranty.duration`.
 */
const REQUIRED_LEAF_SEGMENTS = new Set(['value', 'unit']);

/**
 * `pricing` se suelta entero. El back exige `basePrice` en el body por el
 * esquema del objeto, así que soltar `listPrice` solo no es una operación
 * simple — y soltar `basePrice` solo está prohibido. Tratar el bloque como una
 * unidad es lo único que no miente sobre lo que va a pasar.
 */
const PRICING_POINTER = '/attributes/pricing';

/**
 * Qué pointer hay que soltar para "devolver a la fuente" el campo pedido.
 * `null` = este campo no se puede soltar de ninguna manera.
 *
 * Devuelve el pointer PADRE cuando la hoja es obligatoria: soltar una medición
 * implica soltar la medición entera (valor y unidad juntos), que es como el
 * back la trata.
 */
export function releaseTargetPointer(pointer: string): string | null {
  if (NEVER_RELEASABLE.has(pointer)) return null;
  if (pointer === PRICING_POINTER || pointer.startsWith(`${PRICING_POINTER}/`)) {
    return PRICING_POINTER;
  }

  const segments = pointer.split('/').filter(Boolean);
  if (segments.length === 0) return null;

  const last = segments[segments.length - 1]!;
  if (!REQUIRED_LEAF_SEGMENTS.has(last)) return pointer;

  // Hoja obligatoria: sube al objeto que la contiene. Si al subir queda una
  // raíz intocable (`/attributes`), no hay nada que soltar.
  const parent = `/${segments.slice(0, -1).join('/')}`;
  if (NEVER_RELEASABLE.has(parent) || parent === '/') return null;
  return parent;
}

/** ¿Este campo muestra botón de "volver a la fuente"? */
export function canReleaseToSource(
  pointer: string,
  sources: readonly string[] | undefined,
): boolean {
  // Solo tiene sentido cuando el valor lo está poniendo master: si gana otra
  // fuente, no hay override que borrar.
  if (!sources || sources.length !== 1 || sources[0] !== 'master') return false;
  return releaseTargetPointer(pointer) !== null;
}

/**
 * Body del PATCH que suelta un pointer: el mismo camino anidado con `null` en
 * la punta. `/attributes/isWebActive` -> `{ attributes: { isWebActive: null } }`.
 */
export function buildReleasePatch(pointer: string): Record<string, unknown> {
  const target = releaseTargetPointer(pointer);
  if (target === null) {
    throw new Error(`El campo ${pointer} no se puede devolver a la fuente`);
  }
  const segments = target.split('/').filter(Boolean);
  const body: Record<string, unknown> = {};
  let node = body;
  segments.forEach((segment, index) => {
    if (index === segments.length - 1) {
      node[segment] = null;
      return;
    }
    const next: Record<string, unknown> = {};
    node[segment] = next;
    node = next;
  });
  return body;
}

/**
 * ¿El botón suelta MÁS de lo que dice su campo? Pasa con las mediciones (cae
 * valor y unidad) y con el bloque de precio. La confirmación tiene que decirlo:
 * alguien que aprieta en "Precio base" no espera perder también el precio de
 * lista.
 */
export function releaseAffectsWholeObject(pointer: string): boolean {
  const target = releaseTargetPointer(pointer);
  return target !== null && target !== pointer;
}
